import { embeds } from "@/lib/business";
import { useExternalScript } from "@/hooks/use-external-script";
import { useInViewport } from "@/hooks/use-in-viewport";

interface Props {
  /**
   * Suffix appended to the iframe DOM id so the hero copy and the popup copy
   * never collide. Both instances keep the identical src, data-form-id,
   * width and natural height, so they render pixel-for-pixel the same.
   */
  instance?: string;
  className?: string;
}

/**
 * LeadConnector inline estimate form, embedded exactly as supplied.
 *
 * Deliberately NOT wrapped in any element that sets a min-height, max-height,
 * fixed height or overflow rule. The provider script resizes the iframe from
 * inside; anything that constrains it here would clip the form or introduce
 * an internal scrollbar.
 *
 * The iframe and the provider script are held back until the embed comes
 * within 600px of the viewport. The placeholder reserves the form's exact
 * declared height, so nothing shifts when it swaps in.
 */
const FormEmbed = ({ instance = "hero", className = "" }: Props) => {
  /*
   * A tighter margin than the other deferred embeds. On desktop the form
   * shares the hero row and is in view immediately, so this changes nothing
   * there. On phones the hero stacks and the form lands a screen further
   * down, where loading a third-party iframe — and the cookies, phone-input
   * library and stylesheet that come with it — before the visitor has
   * scrolled anywhere near it is pure overhead.
   */
  const { ref, inView } = useInViewport<HTMLDivElement>({ rootMargin: "200px" });

  useExternalScript(inView ? embeds.form.script : "");

  const iframeId = `inline-${embeds.form.id}-${instance}`;

  return (
    <div ref={ref} className={`widget-embed ${className}`}>
      {inView ? (
        <iframe
          src={embeds.form.src}
          style={{
            width: "100%",
            height: `${embeds.form.height}px`,
            border: "none",
            borderRadius: "10px",
          }}
          id={iframeId}
          data-layout='{"id":"INLINE"}'
          data-trigger-type="alwaysShow"
          data-trigger-value=""
          data-activation-type="alwaysActivated"
          data-activation-value=""
          data-deactivation-type="neverDeactivate"
          data-deactivation-value=""
          data-form-name={embeds.form.name}
          data-height={embeds.form.height}
          data-layout-iframe-id={iframeId}
          data-form-id={embeds.form.id}
          title={embeds.form.name}
          loading="lazy"
          scrolling="no"
        />
      ) : (
        /* Reserves the exact height the form will occupy so it cannot shift
           the page when it mounts. */
        <div
          aria-hidden="true"
          style={{ width: "100%", height: `${embeds.form.height}px`, borderRadius: "10px" }}
        />
      )}
    </div>
  );
};

export default FormEmbed;
